import React, { useState, useEffect, useContext} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Radio,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Snackbar,
  Chip,
  Alert,
  Box,

} from '@mui/material';
import { AccountCircle, } from '@mui/icons-material';
import { ContextSession } from "../../services/ContextSession";

const styles = {
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: 'white',
    backgroundColor: '#19438C',
    py: 2
  },
  currentProcuradorBox: {
    mb: 2,
    p: 2,
    backgroundColor: '#f5f5f5',
    borderRadius: 1
  },
  listItemButton: {
    px: 2,
    py: 1.5,
    '&:hover': { backgroundColor: 'rgba(107, 197, 208, 0.1)' },
    opacity: 1
  },
  disabledListItemButton: {
    px: 2,
    py: 1.5,
    '&:hover': { backgroundColor: 'inherit' },
    opacity: 0.7
  },
  avatar: {
    bgcolor: '#19438C',
    mr: 2,
    width: 32,
    height: 32
  },
  disabledAvatar: {
    bgcolor: 'grey.400',
    mr: 2,
    width: 32,
    height: 32
  },
  dialogActions: {
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 2,
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    mr: 2,
    color: '#f44336',
    fontWeight: 'bold'
  },
  confirmButton: {
    backgroundColor: '#19438C',
    color: 'white',
    '&:hover': { backgroundColor: '#5ab4be' },
    minWidth: 120
  }
};

const ReasignarProcuradorModal = ({
  open,
  onClose,
  onConfirm,
  demandaId,
  procuradorActual,
  procuradoresLista
}) => {
  const [procuradorSeleccionado, setProcuradorSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [procuradorActualMapeado, setProcuradorActualMapeado] = useState(null);
  let sessi = useContext(ContextSession);

  // Mapear el procurador actual al abrir el modal
  useEffect(() => {
    if (open && procuradoresLista.length > 0) {
      setProcuradorSeleccionado(null);
      setError(null);
      let procuradorMapeado = null;
  
      // Buscar por ID
      if (procuradorActual?.id != null) {
        procuradorMapeado = procuradoresLista.find(
          p => String(p.id) === String(procuradorActual.id)
        );
        console.log('🔍 Procurador mapeado por ID:', procuradorMapeado);
      }
  
      // Si no lo encontró, buscar por nombre
      if (!procuradorMapeado && procuradorActual?.nombre) {
        procuradorMapeado = procuradoresLista.find(
          p => p.nombre.trim().toLowerCase() === procuradorActual.nombre.trim().toLowerCase()
        );
        console.log('🔍 Procurador mapeado por nombre:', procuradorMapeado);
      }
      if (!procuradorMapeado) {
        if (typeof procuradorActual === 'string') {
          procuradorMapeado = {
            id: null,
            nombre: procuradorActual,
          };
        } else {
          procuradorMapeado = procuradorActual;
          
        }
      }
      setProcuradorActualMapeado(procuradorMapeado);
    }
  }, [open, procuradorActual, procuradoresLista]);
  

  const handleSeleccionarProcurador = (procurador) => {
    // No hacer nada si es el procurador actual
    if (procuradorActualMapeado && 
        (procurador.id === procuradorActualMapeado.id || 
         procurador.nombre === procuradorActualMapeado.nombre)) {
      return;
    }
    setProcuradorSeleccionado(procurador);
  };

  const esProcuradorActual = (procurador) => {
    if (!procuradorActualMapeado) return false;
    
    // Comparar por ID si existe
    if (procuradorActualMapeado.id && procurador.id === procuradorActualMapeado.id) {
      return true;
    }
    
    // Comparar por nombre si no hay ID
    if (!procuradorActualMapeado.id && procurador.nombre === procuradorActualMapeado.nombre) {
      return true;
    }
    
    return false;
  };

  const handleConfirmar = async () => {
    if (!procuradorSeleccionado) {
      setError('Debe seleccionar un procurador');
      return;
    }
  
    setLoading(true);
    try {
      const result = await onConfirm(procuradorSeleccionado.id);
      
      if (result.success) {
        setSuccess('Procurador reasignado correctamente');
        setTimeout(() => {
          onClose(true);
        }, 1500); 
      } else {
        setError(result.message || 'Error al reasignar procurador');
      }
    } catch (err) {
      setError('Ocurrió un error inesperado');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '100%',
            maxHeight: '90vh',
            height: '500px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', 
          }
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          Reasignar Procurador - Demanda #{demandaId}
        </DialogTitle>
        
        <DialogContent sx={{ 
              flex: 1,          
              overflowY: 'auto', 
              pt: 3, 
              pb: 1,
              minHeight: '200px', 
            }}>
                  
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione el nuevo procurador:
          </Typography>
  
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
  
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <List disablePadding>
              {procuradoresLista.length === 0 ? (
                <Box textAlign="center" p={3}>
                  <Typography color="textSecondary">No hay procuradores disponibles</Typography>
                </Box>
              ) : (
                procuradoresLista.map((procurador) => {
                  const isCurrent = esProcuradorActual(procurador);
                  const isSelected = procuradorSeleccionado?.id === procurador.id;
                  
                  return (
                    <ListItem key={procurador.id} disablePadding>
                      <ListItemButton
                        onClick={() => !isCurrent && handleSeleccionarProcurador(procurador)}
                        disabled={isCurrent || loading}
                        sx={isCurrent ? styles.disabledListItemButton : styles.listItemButton}
                      >
                        <Radio
                          checked={isSelected}
                          disabled={isCurrent}
                          sx={{
                            color: isCurrent ? 'grey.500' : '#19438C',
                            '&.Mui-checked': {
                              color: isCurrent ? 'grey.500' : '#FF0000',
                            },
                          }}
                        />
                        
                        <AccountCircle
                          sx={{
                            fontSize: 40,
                            color: isCurrent ? 'grey.500' : '#19438C',
                            marginRight: 2,
                            ...(isSelected && {
                              color: isCurrent ? 'grey.500' : 'grey',
                            }),
                          }}
                        />                       
                        <ListItemText
                          primary={
                            <>
                              {procurador.nombre.toUpperCase()}
                              {isCurrent && (
                                <Chip 
                                  label="Actual" 
                                  size="small" 
                                  sx={{ 
                                    ml: 1, 
                                    backgroundColor: 'grey.300',
                                    color: 'grey.700',
                                    fontSize: '0.65rem'
                                  }} 
                                />
                              )}
                            </>
                          }
                          primaryTypographyProps={{
                            fontWeight: 'medium',
                            color: isCurrent ? 'text.disabled' : 'text.primary'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </DialogContent>
        
        <DialogActions sx={styles.dialogActions}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button 
              onClick={() => onClose(false)}
              disabled={loading}
              sx={styles.cancelButton}
            >
              Cancelar
            </Button>
            
            <Button 
              variant="contained"
              onClick={handleConfirmar}
              disabled={loading || !procuradorSeleccionado}
              sx={styles.confirmButton}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : 'Confirmar Reasignación'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
  
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
    </>
  );
};

export default ReasignarProcuradorModal;